import { useEffect, useState } from 'react';
import type { LevelDoc } from '../model/format.ts';
import { worldToScreen } from '../render/renderer.ts';
import { findObject, useEditor } from '../editor/store.ts';
import { Icon } from '../editor/widgets.tsx';
import { useSettings } from '../editor/settings.ts';
import { assignSlot, claimSlot, createLobby, joinLobby, kickMember, leaveLobby, sendCursor, transferHost, useLobby } from './client.ts';
import { MAX_NAME, cleanColor, type MemberView } from './protocol.ts';
import { useSession } from '../auth/session.ts';

const COLORS = ['#6ec1ff', '#ffb020', '#7ddea0', '#ff8ad4', '#c9a6ff', '#ff7a7a'];
/** cursor colours to pick from (the derived ones first) */
const PRESETS = [...COLORS, '#3ee6d8', '#f5e663', '#ff9b4a', '#9aa8ff', '#ffffff', '#b4ff5c'];

function colorFor(id: string): string {
  let hash = 0;
  for (const ch of id) hash = (hash * 33 + ch.charCodeAt(0)) >>> 0;
  return COLORS[hash % COLORS.length];
}

function who(email: string | null): string {
  return email ?? 'Player';
}

const memberColor = (m: Pick<MemberView, 'id' | 'color'>) => m.color ?? colorFor(m.id);
const shortName = (email: string | null) => (email ? email.split('@')[0] : '') || 'Player';
const memberName = (m: Pick<MemberView, 'email' | 'name'>) => m.name ?? shortName(m.email);

/** Dark text on light colours, white on dark ones. */
function inkOn(hex: string): string {
  const v = parseInt(hex.slice(1), 16);
  const [r, g, b] = [(v >> 16) & 255, (v >> 8) & 255, v & 255];
  return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? '#111318' : '#ffffff';
}

export function LobbyButton() {
  const lobby = useLobby();
  const set = useEditor((s) => s.set);
  const inRoom = !!lobby.room;
  return (
    <button className={`icon-btn wide-only text-btn ${inRoom ? 'on' : ''}`} title={inRoom ? 'Lobby' : 'Edit and play a level together'} onClick={() => set({ modal: 'lobby' })}>
      <Icon name="people" />
      <span>Lobby</span>
    </button>
  );
}

export function LobbyBar() {
  const lobby = useLobby();
  const room = lobby.room;
  const set = useEditor((s) => s.set);
  const min = useSettings((s) => s.lobbyBarMin);
  const update = useSettings((s) => s.update);
  if (!room) return null;
  const mine = room.members.find((member) => member.id === room.self);
  const host = room.hostId === room.self;
  const selfColor = mine ? memberColor(mine) : colorFor(room.self);
  if (min)
    return (
      <div className="lobby-bar min">
        <span className="lobby-dot" style={{ background: selfColor }} />
        <span>{room.members.length}</span>
        {lobby.status !== 'live' && <span>Reconnecting…</span>}
        <button className="icon-btn" title="Show the lobby bar" onClick={() => update({ lobbyBarMin: false })}><Icon name="people" size={16} /></button>
      </div>
    );
  return (
    <div className="lobby-bar">
      <span className="lobby-dot" style={{ background: selfColor }} />
      <b>{room.levelName}</b>
      <span>{host ? 'You are hosting' : `Hosted by ${hostName(room.members)}`}</span>
      <span>{room.members.length} here</span>
      <span>{room.mode === 'play' ? 'Playing' : mine?.slot == null ? 'Spectating' : `Player ${mine.slot + 1}`}</span>
      {lobby.status !== 'live' && <span>Reconnecting…</span>}
      <button className="chip" onClick={() => set({ modal: 'lobby' })}>People</button>
      <button className="chip" onClick={leaveLobby}>Leave</button>
      <button className="icon-btn" title="Minimise" onClick={() => update({ lobbyBarMin: true })}><Icon name="minus" size={16} /></button>
    </div>
  );
}

export function LobbyCursors() {
  const lobby = useLobby();
  const doc = useEditor((s) => s.doc);
  const camera = useEditor((s) => s.camera);
  const selection = useEditor((s) => s.selection);
  const room = lobby.room;
  useEffect(() => {
    if (!room || room.mode === 'play') return;
    let last = 0;
    const send = (ev: PointerEvent) => {
      const now = performance.now();
      if (now - last < 50) return;
      last = now;
      const stage = document.querySelector('.stage');
      if (!stage) return;
      const rect = stage.getBoundingClientRect();
      const wx = (ev.clientX - rect.left - rect.width / 2) / camera.zoom + camera.x;
      const wy = -((ev.clientY - rect.top) - rect.height / 2) / camera.zoom + camera.y;
      sendCursor(wx, wy, selectionIds(selection));
    };
    window.addEventListener('pointermove', send);
    return () => window.removeEventListener('pointermove', send);
  }, [room, camera, selection]);
  if (!room || room.mode === 'play' || !doc) return null;
  return (
    <div className="lobby-cursors">
      {lobby.cursors.map((cursor) => {
        const member = room.members.find((m) => m.id === cursor.accountId);
        if (!member) return null;
        const stage = typeof document !== 'undefined' ? document.querySelector('.stage') : null;
        const w = stage?.clientWidth ?? 1;
        const h = stage?.clientHeight ?? 1;
        const [sx, sy] = worldToScreen(camera, w, h, cursor.x, cursor.y);
        const marks = cursor.sel.map((id) => marker(doc, id)).filter((p): p is [number, number] => !!p);
        const color = memberColor(member);
        return (
          <div key={cursor.accountId}>
            {marks.map((p, i) => {
              const [mx, my] = worldToScreen(camera, w, h, p[0], p[1]);
              return <span key={i} className="lobby-mark" style={{ left: mx, top: my, borderColor: color }} />;
            })}
            <div className="lobby-cursor" style={{ transform: `translate(${sx}px, ${sy}px)` }}>
              <span className="lobby-cursor-name" style={{ background: color, color: inkOn(color) }} title={who(member.email)}>
                {memberName(member)}
              </span>
              <CursorArrow color={color} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** A pointer arrow with its tip at the top left corner. */
function CursorArrow({ color }: { color: string }) {
  return (
    <svg className="lobby-cursor-arrow" width="18" height="22" viewBox="0 0 18 22" aria-hidden>
      <path d="M1.5 1.5 L1.5 17.5 L5.6 13.6 L8.6 20 L11.6 18.6 L8.7 12.4 L14.5 12.4 Z" fill={color} stroke="#111318" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

/** Your cursor colour and name for lobbies, kept in the settings. */
function ProfilePicker() {
  const color = useSettings((s) => s.lobbyColor);
  const name = useSettings((s) => s.lobbyName);
  const update = useSettings((s) => s.update);
  const session = useSession();
  const room = useLobby().room;
  const self = room?.self ?? session?.id ?? '';
  const email = room?.members.find((m) => m.id === room.self)?.email ?? session?.email ?? null;
  const shown = cleanColor(color) ?? (self ? colorFor(self) : PRESETS[0]);
  return (
    <div className="lobby-profile">
      <div className="lobby-profile-preview">
        <span className="lobby-cursor-name" style={{ background: shown, color: inkOn(shown) }}>{name.trim() || shortName(email)}</span>
        <CursorArrow color={shown} />
      </div>
      <div className="lobby-profile-fields">
        <label>
          <span className="hint">Name above your cursor</span>
          <input type="text" maxLength={MAX_NAME} placeholder={shortName(email)} value={name} onChange={(ev) => update({ lobbyName: ev.target.value })} />
        </label>
        <div className="lobby-swatches" role="radiogroup" aria-label="Cursor colour">
          <button className={`lobby-swatch auto ${!cleanColor(color) ? 'on' : ''}`} title="Pick one for me" aria-checked={!cleanColor(color)} role="radio" onClick={() => update({ lobbyColor: '' })}>
            Auto
          </button>
          {PRESETS.map((c) => (
            <button key={c} className={`lobby-swatch ${cleanColor(color) === c ? 'on' : ''}`} style={{ background: c }} title={c} aria-checked={cleanColor(color) === c} role="radio" onClick={() => update({ lobbyColor: c })} />
          ))}
          <label className={`lobby-swatch custom ${cleanColor(color) && !PRESETS.includes(cleanColor(color)!) ? 'on' : ''}`} title="Any colour">
            <input type="color" value={shown} onChange={(ev) => update({ lobbyColor: ev.target.value })} />
          </label>
        </div>
      </div>
    </div>
  );
}

function hostName(members: MemberView[]): string {
  const host = members.find((member) => member.host);
  return host ? memberName(host) : 'Player';
}

function selectionIds(sel: ReturnType<typeof useEditor.getState>['selection']): string[] {
  if (sel.kind === 'objects') return sel.ids;
  if (sel.kind === 'body' || sel.kind === 'terrain') return [sel.id];
  if (sel.kind === 'waypoint') return [sel.body];
  return [];
}

function marker(doc: LevelDoc, id: string): [number, number] | null {
  const found = findObject(doc, id);
  if (found) return [found.obj.x, found.obj.y];
  const body = doc.bodies.find((item) => item.id === id);
  if (body) return body.origin;
  return null;
}

export function LobbyDialog() {
  const lobby = useLobby();
  const close = () => useEditor.getState().set({ modal: null });
  const room = lobby.room;
  return (
    <div className="modal-back" onClick={close}>
      <div className="modal" onClick={(ev) => ev.stopPropagation()}>
        <div className="modal-head">
          <h2>{room ? 'Lobby' : 'Lobbies'}</h2>
          <button className="icon-btn" onClick={close}><Icon name="close" /></button>
        </div>
        <div className="modal-body">
          {room ? <Manager /> : <Browser />}
        </div>
      </div>
    </div>
  );
}

function Browser() {
  const lobby = useLobby();
  const doc = useEditor((s) => s.doc);
  const [busy, setBusy] = useState(false);
  return (
    <>
      <ProfilePicker />
      <p className="hint">Host the open level, or join someone. The host keeps the level, applies edits, and runs the game. Tile cases and custom tiles you fix or draw are shared with everyone in the lobby.</p>
      {lobby.status !== 'live' && <p className="hint">Connecting to the lobby…</p>}
      <button
        className="btn"
        disabled={!doc || busy || lobby.status !== 'live'}
        onClick={() => {
          setBusy(true);
          createLobby().finally(() => setBusy(false));
        }}
      >
        Host this level
      </button>
      <div className="account-list">
        {lobby.lobbies.length === 0 && <p className="hint">No open lobbies.</p>}
        {lobby.lobbies.map((item) => (
          <div key={item.id} className="account-row">
            <div className="who">
              <b>{item.levelName}</b>
              <span>{who(item.hostEmail)} · {item.members} people · {item.players} playing · {item.mode === 'play' ? 'in play' : 'editing'}</span>
            </div>
            <button className="btn" onClick={() => void joinLobby(item.id)}>Join</button>
          </div>
        ))}
      </div>
    </>
  );
}

function Manager() {
  const lobby = useLobby();
  const room = lobby.room;
  if (!room) return null;
  const host = room.hostId === room.self;
  const playing = room.mode === 'play';
  return (
    <>
      <p className="hint">
        {playing
          ? 'Play is one shared game. The host simulates it. Switch hosts after play ends, because a new machine cannot resume a jump already in the air.'
          : 'Edits go to the host, then back out to everyone. A drag locks that object until you let go. Undo works only if nobody has changed the level since your edit. Up to four players. Each person can play more than one, with another keyboard or gamepad.'}
      </p>
      <ProfilePicker />
      <div className="account-list">
        {room.members.map((member) => (
          <div key={member.id} className="account-row">
            <span className="lobby-dot" style={{ background: memberColor(member) }} />
            <div className="who">
              <b title={who(member.email)}>{memberName(member)}{member.id === room.self ? ' (you)' : ''}{member.host ? ' · host' : ''}</b>
              <span>{member.slots.length === 0 ? 'Spectating' : member.slots.length === 1 ? `Player ${member.slots[0] + 1}` : `Players ${member.slots.map((slot) => slot + 1).join(', ')}`}</span>
            </div>
            {member.id === room.self && (
              <select disabled={playing} value={member.slot ?? ''} onChange={(ev) => claimSlot(ev.target.value === '' ? null : Number(ev.target.value))}>
                <option value="">Spectate</option>
                {[0, 1, 2, 3].map((slot) => <option key={slot} value={slot}>Player {slot + 1}</option>)}
              </select>
            )}
            {host && member.id !== room.self && (
              <>
                <select disabled={playing} value={member.slot ?? ''} onChange={(ev) => assignSlot(member.id, ev.target.value === '' ? null : Number(ev.target.value))}>
                  <option value="">Spectate</option>
                  {[0, 1, 2, 3].map((slot) => <option key={slot} value={slot}>Player {slot + 1}</option>)}
                </select>
                <button className="btn" disabled={playing} title={playing ? 'Switch hosts after play ends' : 'They become the host'} onClick={() => transferHost(member.id)}>Make host</button>
                <button className="btn" onClick={() => kickMember(member.id)}>Remove</button>
              </>
            )}
          </div>
        ))}
      </div>
      <button className="btn" onClick={leaveLobby}>Leave lobby</button>
    </>
  );
}
